import React, { useMemo } from "react";
import { Switch, InputNumber, Tooltip } from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  StarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import AdminAvatar from "../AdminAvatar/AdminAvatar";
import StatusTag from "../StatusTag/StatusTag";
import PriorityTag from "../PriorityTag/PriorityTag";
import {
  TABLE_COLUMN_WIDTHS,
  UI_DIMENSIONS,
  DAILY_BOOKING_LIMITS,
  INFO_MESSAGES,
} from "../../constants/adminBookingConstants";

/**
 * Creates table column definitions for admin booking configuration table.
 * @param {Object} params
 * @param {Function} params.handleAdminConfigChange - Config change handler
 * @param {boolean} params.loading - Loading state
 * @param {boolean} params.saving - Saving state
 * @param {boolean} params.roundRobinEnabled - Round Robin mode status
 * @param {boolean} params.canUpdate - User update permission
 * @returns {Array} Ant Design table columns
 */
export const createTableColumns = ({
  handleAdminConfigChange,
  loading,
  saving,
  roundRobinEnabled,
  canUpdate = false,
}) => {
  // Memoize the switch disabled state
  const switchDisabled = loading || saving || !canUpdate;
  const dailyLimitDisabled = loading || saving || roundRobinEnabled || !canUpdate;
  const highPrioritySwitchDisabled = loading || saving || roundRobinEnabled || !canUpdate;

  return [
    {
      title: (
        <span style={{ display: "flex", alignItems: "center" }}>
          <UserOutlined style={{ marginRight: 10, color: "#52c41a", fontSize: 16 }} />
          Admin Name
        </span>
      ),
      dataIndex: "adminName",
      key: "adminName",
      width: TABLE_COLUMN_WIDTHS.ADMIN_NAME,
      render: (name) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AdminAvatar name={name} />
          <span style={{ fontWeight: 600, color: "#262626", fontSize: 15 }}>{name}</span>
        </div>
      ),
    },
    {
      title: (
        <span>
          <CheckCircleOutlined style={{ marginRight: 8, color: "#52c41a" }} />
          Active
        </span>
      ),
      dataIndex: "isActive",
      key: "isActive",
      width: TABLE_COLUMN_WIDTHS.ACTIVE,
      align: "center",
      render: (isActive, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            minHeight: "32px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: `${UI_DIMENSIONS.SWITCH_WIDTH}px`,
              flexShrink: 0,
            }}
          >
            <Tooltip title={!canUpdate ? INFO_MESSAGES.NO_UPDATE_PERMISSION : ""}>
              <Switch
                checked={isActive}
                onChange={(checked) =>
                  handleAdminConfigChange(record.id, "isActive", checked)
                }
                disabled={switchDisabled}
              />
            </Tooltip>
          </div>
          <StatusTag isActive={isActive} />
        </div>
      ),
    },
    {
      title: (
        <span>
          <StarOutlined style={{ marginRight: 8, color: "#faad14" }} />
          High Priority
        </span>
      ),
      dataIndex: "isHighPriority",
      key: "isHighPriority",
      width: TABLE_COLUMN_WIDTHS.HIGH_PRIORITY,
      align: "center",
      render: (isHighPriority, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            minHeight: "32px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: `${UI_DIMENSIONS.SWITCH_WIDTH}px`,
              flexShrink: 0,
            }}
          >
            <Tooltip
              title={
                !canUpdate
                  ? INFO_MESSAGES.NO_UPDATE_PERMISSION
                  : roundRobinEnabled
                  ? INFO_MESSAGES.ROUND_ROBIN_DISABLED
                  : ""
              }
            >
              <Switch
                checked={isHighPriority}
                onChange={(checked) =>
                  handleAdminConfigChange(record.id, "isHighPriority", checked)
                }
                disabled={highPrioritySwitchDisabled}
              />
            </Tooltip>
          </div>
          <PriorityTag
            isHighPriority={isHighPriority}
            roundRobinEnabled={roundRobinEnabled}
          />
        </div>
      ),
    },
    {
      title: (
        <span>
          <CalendarOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          Max Daily Limit
        </span>
      ),
      dataIndex: "maxDailyLimit",
      key: "maxDailyLimit",
      width: TABLE_COLUMN_WIDTHS.MAX_DAILY_LIMIT,
      align: "center",
      render: (maxDailyLimit, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Tooltip
            title={
              !canUpdate
                ? INFO_MESSAGES.NO_UPDATE_PERMISSION
                : roundRobinEnabled
                ? INFO_MESSAGES.ROUND_ROBIN_DISABLED
                : ""
            }
          >
            <InputNumber
              min={DAILY_BOOKING_LIMITS.MIN}
              max={DAILY_BOOKING_LIMITS.MAX}
              value={maxDailyLimit}
              onChange={(value) =>
                handleAdminConfigChange(
                  record.id,
                  "maxDailyLimit",
                  value || DAILY_BOOKING_LIMITS.DEFAULT
                )
              }
              disabled={dailyLimitDisabled}
              style={{ width: `${UI_DIMENSIONS.INPUT_NUMBER_WIDTH}px`, fontWeight: 600 }}
              prefix=""
            />
          </Tooltip>
          <span style={{ color: "#8c8c8c", fontSize: 12, fontWeight: 500 }}>per day</span>
        </div>
      ),
    },
  ];
};
